/**
 * PersonPanel — slides in from the right when a tree node is clicked.
 * Shows person summary, family, documents, and Origin AI context.
 *
 * Origin shows contextual suggestion chips on open. Streaming only starts
 * when the user clicks a chip or the "Research" button — never automatically.
 * Cached results (from prior sessions) appear instantly on chip click.
 */
import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, mediaUrl, API_BASE_URL } from '../../services/api.js'
import { useTreeStore } from '../../stores/tree.js'
import { useAuthStore } from '../../stores/auth.js'
import { extractYear } from '../../utils/date.js'
import EnrichFromKGModal from '../person/EnrichFromKGModal.jsx'
import './PersonPanel.css'

function getUserId(token) {
  try { return JSON.parse(atob(token.split('.')[1])).sub } catch { return null }
}

// Build 2-3 contextual suggestion chips from person data — no AI call needed
function buildSuggestions(person) {
  const chips = []
  const name = person.preferred_name || person.first_name || 'this person'
  const birthYear = extractYear(person.birth_date)
  const place = person.birth_place

  if (place && birthYear) {
    chips.push(`What was life like in ${place} around ${birthYear}?`)
  } else if (place) {
    chips.push(`What was ${place} like historically?`)
  }

  if (person.occupation) {
    chips.push(`What did being a ${person.occupation} mean in that era?`)
  } else if (birthYear) {
    chips.push(`What major events shaped ${name}'s lifetime?`)
  }

  if (place) {
    chips.push(`What records might exist for ${place}?`)
  } else {
    chips.push(`What historical records might document ${name}?`)
  }

  return chips.slice(0, 3)
}

export default function PersonPanel({ personId, treeId, tree, onClose }) {
  const navigate = useNavigate()
  const { persons, relationships, fetchTree } = useTreeStore()
  const { token } = useAuthStore()

  const [person, setPerson] = useState(null)
  const [docs, setDocs] = useState([])
  const [originText, setOriginText] = useState('')
  const [originStreaming, setOriginStreaming] = useState(false)
  const [originDone, setOriginDone] = useState(false)
  const [loadingDocs, setLoadingDocs] = useState(true)
  const [enriching, setEnriching] = useState(false)
  const abortRef = useRef(null)

  const isOwner = tree?.owner_id === getUserId(token)

  // Load person + docs whenever personId changes — no Origin call
  useEffect(() => {
    if (!personId) return

    setPerson(null)
    setDocs([])
    setOriginText('')
    setOriginDone(false)
    setOriginStreaming(false)
    setLoadingDocs(true)

    if (abortRef.current) abortRef.current.abort()

    api.get(`/persons/${personId}`).then(setPerson).catch(() => {})
    api.get(`/persons/${personId}/documents`)
      .then(setDocs).catch(() => {}).finally(() => setLoadingDocs(false))

    return () => { if (abortRef.current) abortRef.current.abort() }
  }, [personId])

  function triggerOrigin() {
    if (originStreaming || originDone) return
    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller

    streamOriginContext(personId, token, controller.signal, {
      onStart: () => setOriginStreaming(true),
      onChunk: chunk => setOriginText(prev => prev + chunk),
      onCached: text => { setOriginText(text); setOriginDone(true); setOriginStreaming(false) },
      onDone: () => { setOriginDone(true); setOriginStreaming(false) },
      onError: () => { setOriginDone(true); setOriginStreaming(false) }
    })
  }

  // Derive relationships from tree store.
  //
  // Edges are canonically stored as person_a = parent, person_b = child, but
  // some imports wind up reversed. To avoid losing children (or misreporting
  // them as parents), we classify a parent_child edge using birth-year when
  // available: whichever end is older is treated as the parent. We fall back
  // to the canonical direction when either year is missing.
  const personMap = new Map(persons.map(p => [p.id, p]))
  const selfYear = Number(extractYear(person?.birth_date))

  const parentSet = new Map()
  const childSet = new Map()
  for (const r of relationships) {
    if (r.type !== 'parent_child') continue
    if (r.person_a_id !== personId && r.person_b_id !== personId) continue
    const otherId = r.person_a_id === personId ? r.person_b_id : r.person_a_id
    const other = personMap.get(otherId)
    if (!other) continue
    const otherYear = Number(extractYear(other.birth_date))
    const canonicallyParent = r.person_a_id === otherId  // other is person_a → canonical parent
    let treatAsParent
    if (selfYear && otherYear) {
      treatAsParent = otherYear < selfYear  // older person is the parent
    } else {
      treatAsParent = canonicallyParent
    }
    if (treatAsParent) parentSet.set(otherId, other)
    else childSet.set(otherId, other)
  }
  const parents  = [...parentSet.values()]
  const children = [...childSet.values()]

  // Partner dedup: collapse by the other-person ID (guards against two
  // partner rows pointing at the same person) AND by (name + birth_year)
  // key (guards against duplicate person records created across imports).
  const partnerById = new Map()
  const partnerByKey = new Set()
  for (const r of relationships) {
    if (r.type !== 'partner') continue
    if (r.person_a_id !== personId && r.person_b_id !== personId) continue
    const otherId = r.person_a_id === personId ? r.person_b_id : r.person_a_id
    const other = personMap.get(otherId)
    if (!other) continue
    if (partnerById.has(other.id)) continue
    const key = [
      (other.first_name || '').toLowerCase().trim(),
      (other.last_name || '').toLowerCase().trim(),
      extractYear(other.birth_date) || '',
    ].join('|')
    if (partnerByKey.has(key)) continue
    partnerById.set(other.id, other)
    partnerByKey.add(key)
  }
  const partners = [...partnerById.values()]

  const displayFirst = person?.preferred_name || person?.first_name || ''
  const birthYear = extractYear(person?.birth_date)
  const deathYear = extractYear(person?.death_date)
  const yearStr = birthYear ? (deathYear ? `${birthYear} – ${deathYear}` : `b. ${birthYear}`) : null

  if (!personId) return null

  // Public profile lives on the landing site (genealogic.ai/person/<slug>).
  // Every person with a slug has a public page now — living people get a
  // redacted, limited-info version — so the link shows whenever a slug exists.
  const publicUrl = person?.public_slug
    ? `https://genealogic.ai/person/${person.public_slug}`
    : null

  return (
    <div className="person-panel">
      {/* Floating controls. "Public profile" leaves the app for the public
          page on genealogic.ai (new tab); the in-app full page is reached via
          "Open full page" in the footer — kept distinct so owners don't land
          on the unauthenticated public page expecting the in-app profile. */}
      <div className="person-panel__controls">
        {publicUrl && (
          <a
            className="person-panel__open-full"
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            title="View the public page on genealogic.ai (opens in a new tab)"
          >↗ Public profile</a>
        )}
        <button className="person-panel__close" onClick={onClose} title="Close">✕</button>
      </div>

      <div className="person-panel__scroll">
        {!person ? (
          <div className="person-panel__loading">Loading…</div>
        ) : (
          <>
            {/* Identity */}
            <div className="person-panel__identity">
              {person.photo_thumb_key ? (
                <img
                  className="person-panel__photo"
                  src={mediaUrl(person.photo_thumb_key)}
                  alt={displayFirst}
                />
              ) : (
                <div className="person-panel__avatar">
                  {displayFirst[0]?.toUpperCase() || '?'}
                </div>
              )}
              <div>
                <div className="person-panel__name">
                  {displayFirst}
                  {person.last_name && <span className="person-panel__surname"> {person.last_name}</span>}
                </div>
                {person.first_name && person.preferred_name && (
                  <div className="person-panel__formal">{person.first_name} {person.last_name}</div>
                )}
                {yearStr && <div className="person-panel__years">{yearStr}</div>}
                {person.birth_place && <div className="person-panel__place">{person.birth_place}</div>}
                {person.occupation && <div className="person-panel__occupation">{person.occupation}</div>}
              </div>
            </div>

            {/* Enrich from the shared Knowledge Graph — owner-only. The whole
                point is surfacing relatives + details OTHER researchers already
                recorded, so it shows even when this person looks complete. */}
            {isOwner && (
              <button
                className="person-panel__enrich-cta"
                onClick={() => setEnriching(true)}
              >
                ✨ Enrich from Knowledge Graph
              </button>
            )}

            {/* Family */}
            {(parents.length > 0 || children.length > 0 || partners.length > 0) && (
              <div className="person-panel__section">
                <div className="person-panel__section-label">Family</div>
                {parents.length > 0 && (
                  <div className="person-panel__rel-group">
                    <div className="person-panel__rel-type">Parents</div>
                    {parents.map(p => <RelPill key={p.id} person={p} onClick={() => navigate(`/tree/${treeId}/person/${p.id}`)} />)}
                  </div>
                )}
                {partners.length > 0 && (
                  <div className="person-panel__rel-group">
                    <div className="person-panel__rel-type">Partner{partners.length > 1 ? 's' : ''}</div>
                    {partners.map(p => <RelPill key={p.id} person={p} onClick={() => navigate(`/tree/${treeId}/person/${p.id}`)} />)}
                  </div>
                )}
                {children.length > 0 && (
                  <div className="person-panel__rel-group">
                    <div className="person-panel__rel-type">Children</div>
                    {children.map(p => <RelPill key={p.id} person={p} onClick={() => navigate(`/tree/${treeId}/person/${p.id}`)} />)}
                  </div>
                )}
              </div>
            )}

            {/* Documents — with inline intelligence for analysed ones */}
            <div className="person-panel__section">
              <div className="person-panel__section-label">Documents</div>
              {loadingDocs ? (
                <div className="person-panel__muted">Loading…</div>
              ) : docs.length === 0 ? (
                <div className="person-panel__muted">No documents yet.</div>
              ) : (
                <div className="person-panel__docs">
                  {docs.slice(0, 5).map(doc => (
                    <DocCard key={doc.id} doc={doc} />
                  ))}
                  {docs.length > 5 && (
                    <div className="person-panel__muted" style={{ marginTop: '0.25rem' }}>+{docs.length - 5} more</div>
                  )}
                </div>
              )}
            </div>

            {/* Origin */}
            <div className="person-panel__section">
              <div className="person-panel__origin-header">
                <span className="person-panel__origin-badge">Origin</span>
                <span className="person-panel__section-label" style={{ margin: 0 }}>Historical context</span>
                {originStreaming && <span className="person-panel__origin-pulse" />}
              </div>

              {originText ? (
                // Streaming or completed response
                <div className="person-panel__context">
                  {originText}
                  {originStreaming && <span className="person-panel__cursor">▋</span>}
                </div>
              ) : originStreaming ? (
                // Thinking state (between click and first token)
                <div className="person-panel__origin-thinking">
                  <span className="person-panel__origin-dot" />
                  <span className="person-panel__origin-dot" style={{ animationDelay: '0.2s' }} />
                  <span className="person-panel__origin-dot" style={{ animationDelay: '0.4s' }} />
                  <span style={{ marginLeft: '0.4rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                    Researching…
                  </span>
                </div>
              ) : (
                // Idle — show suggestion chips
                <div className="person-panel__origin-idle">
                  <div className="person-panel__origin-chips">
                    {buildSuggestions(person).map((chip, i) => (
                      <button
                        key={i}
                        className="person-panel__origin-chip"
                        onClick={triggerOrigin}
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                  <button className="person-panel__origin-cta" onClick={triggerOrigin}>
                    Research {displayFirst}'s world →
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="person-panel__footer">
              <button
                className="btn btn-primary btn-sm"
                style={{ width: '100%' }}
                onClick={() => navigate(`/tree/${treeId}/person/${personId}`)}
              >
                Open full page →
              </button>
            </div>
          </>
        )}
      </div>

      {enriching && person && (
        <EnrichFromKGModal
          personId={personId}
          treeId={treeId}
          personName={displayFirst || 'this person'}
          onClose={() => setEnriching(false)}
          onApplied={() => {
            // Refresh the panel + the whole tree so imported relatives and
            // updated fields appear immediately (incl. above this person).
            api.get(`/persons/${personId}`).then(setPerson).catch(() => {})
            if (fetchTree) fetchTree(treeId)
          }}
        />
      )}
    </div>
  )
}

// ─── SSE streaming helper ────────────────────────────────────────────────

async function streamOriginContext(personId, token, signal, { onStart, onChunk, onCached, onDone, onError }) {
  try {
    onStart()
    const res = await fetch(`${API_BASE_URL}/ai/context/${personId}/stream`, {
      headers: { Authorization: `Bearer ${token}` },
      signal
    })
    if (!res.ok) { onError(); return }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let sawCached = false

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() // keep incomplete line

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        try {
          const data = JSON.parse(line.slice(6))
          if (data.error) { onError(); return }
          if (data.cached) { onCached(data.text); sawCached = true; continue }
          if (data.text) onChunk(data.text)
          if (data.done) { onDone(); return }
        } catch {}
      }
    }

    // Stream closed without a `done` event — treat as finished so we don't
    // keep showing the "Researching…" indicator forever.
    if (sawCached) onDone()
    else onError()
  } catch (err) {
    if (err.name !== 'AbortError') onError()
  }
}

// ─── Sub-components ──────────────────────────────────────────────────────

function RelPill({ person, onClick }) {
  const name = [person.preferred_name || person.first_name, person.last_name].filter(Boolean).join(' ')
  const year = extractYear(person.birth_date)
  return (
    <button className="person-panel__rel-pill" onClick={onClick}>
      {name}{year ? <span className="person-panel__rel-year"> {year}</span> : ''}
    </button>
  )
}

function docIcon(type) {
  const icons = { birth_cert: '📄', death_cert: '📄', marriage_cert: '💍', census: '📋', military: '🎖️', photo: '🖼️', newspaper: '📰', letter: '✉️', other: '📎' }
  return icons[type] || '📎'
}

function DocCard({ doc }) {
  const [expanded, setExpanded] = useState(false)
  const ef = doc.extracted_fields
  const hasIntelligence = doc.extraction_status === 'done' && ef

  // Non-subject persons found in the document (witnesses, parents, spouses etc.)
  const notablePeople = ef?.persons
    ?.filter(p => p.role !== 'subject' && p.name)
    ?? []

  return (
    <div className={`person-panel__doc-card ${hasIntelligence ? 'person-panel__doc-card--analysed' : ''}`}>
      {/* Always-visible row: icon + name + badge + expand toggle */}
      <div className="person-panel__doc-row">
        <span className="person-panel__doc-icon">{docIcon(doc.doc_type)}</span>
        <a href={doc.url} target="_blank" rel="noreferrer" className="person-panel__doc-name">
          {doc.file_name}
        </a>
        {hasIntelligence && (
          <button
            className="person-panel__doc-expand"
            onClick={() => setExpanded(v => !v)}
            title={expanded ? 'Hide details' : 'Show extracted details'}
          >
            {expanded ? '▲' : '▼'}
          </button>
        )}
        {doc.extraction_status === 'processing' && (
          <span className="person-panel__doc-processing">analysing…</span>
        )}
        {doc.extraction_status === 'done' && !expanded && (
          <span className="person-panel__doc-badge">✓</span>
        )}
      </div>

      {/* Expanded intelligence panel */}
      {hasIntelligence && expanded && (
        <div className="person-panel__doc-intel">
          {/* Meta row: date + place */}
          {(ef.document_date || ef.document_place) && (
            <div className="person-panel__doc-meta">
              {ef.document_date && <span>{ef.document_date}</span>}
              {ef.document_date && ef.document_place && <span className="person-panel__doc-sep">·</span>}
              {ef.document_place && <span>{ef.document_place}</span>}
            </div>
          )}

          {/* Summary */}
          {ef.summary && (
            <div className="person-panel__doc-summary">{ef.summary}</div>
          )}

          {/* Notable persons found */}
          {notablePeople.length > 0 && (
            <div className="person-panel__doc-people">
              {notablePeople.map((p, i) => (
                <div key={i} className="person-panel__doc-person">
                  <span className="person-panel__doc-person-role">{p.role}</span>
                  <span className="person-panel__doc-person-name">{p.name}</span>
                  {p.occupation && <span className="person-panel__doc-person-note">{p.occupation}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Confidence indicator */}
          {ef.confidence && ef.confidence !== 'high' && (
            <div className="person-panel__doc-confidence">
              Confidence: {ef.confidence}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
